import { formatDateTime } from "@/lib/format";

export type TemplateField = {
  id?: string;
  label?: string;
  type?: string;
  role?: string;
  enabled?: boolean;
  required?: boolean;
  suggestedHazards?: string[];
  controlLibrary?: string[];
  defaultValue?: string;
  options?: string[];
};

export type TemplateSection = {
  id?: string;
  title?: string;
  enabled?: boolean;
  fields?: TemplateField[];
};

export type TemplateBlocks = {
  meta?: Record<string, unknown>;
  context?: Record<string, unknown>;
  sections?: TemplateSection[];
};

/**
 * Get the display value for a field based on its type and raw value
 */
export function getFieldDisplayValue(
  field: TemplateField,
  value: unknown
): string {
  if (value === null || value === undefined) {
    return "Not provided";
  }

  switch (field.type) {
    case "text":
    case "textarea":
      return typeof value === "string" ? value : String(value);

    case "checkbox":
      if (typeof value === "boolean") {
        return value ? "✓ Checked" : "✗ Not checked";
      }
      if (typeof value === "string") {
        return value === "true" || value === "1"
          ? "✓ Checked"
          : "✗ Not checked";
      }
      return "✗ Not checked";

    case "datetime":
      if (value instanceof Date) {
        return formatDateTime(value);
      }
      if (typeof value === "string" || typeof value === "number") {
        try {
          const date = new Date(value);
          return Number.isNaN(date.getTime())
            ? String(value)
            : formatDateTime(date);
        } catch {
          return String(value);
        }
      }
      return String(value);

    case "signature":
      // Signatures are handled separately in PeopleInvolvedCard
      return "";

    case "hazard_list":
      // Hazards are handled separately in HazardsControlsCard
      return "";

    default:
      return typeof value === "string" ? value : JSON.stringify(value);
  }
}

/**
 * Check if a field should be rendered
 */
export function shouldRenderField(field: TemplateField): boolean {
  return (
    field.enabled !== false &&
    field.type !== "signature" &&
    field.type !== "hazard_list"
  );
}

/**
 * Get the field label for display
 */
export function getFieldLabel(field: TemplateField, index: number): string {
  return field.label || field.id || `Field ${index + 1}`;
}

/**
 * Check if a section should be rendered
 */
export function shouldRenderSection(section: TemplateSection): boolean {
  return section.enabled !== false;
}

/**
 * Get the section title for display
 */
export function getSectionTitle(
  section: TemplateSection,
  index: number
): string {
  return section.title || `Section ${index + 1}`;
}

/**
 * Get the value for a field from the form blocks, falling back to defaultValue
 * This assumes the form blocks have the same structure as template blocks
 */
export function getFieldValue(
  field: TemplateField,
  formBlocks: Record<string, unknown>
): unknown {
  // For now, assume values are stored in context or meta
  // This might need to be adjusted based on how data is actually stored
  const context = formBlocks.context as Record<string, unknown> | undefined;
  const meta = formBlocks.meta as Record<string, unknown> | undefined;

  // Try context first, then meta
  const existingValue = context?.[field.id || ""] || meta?.[field.id || ""];

  // If there's an existing value, use it
  if (
    existingValue !== undefined &&
    existingValue !== null &&
    existingValue !== ""
  ) {
    return existingValue;
  }

  // Otherwise, use the default value from the field definition
  return field.defaultValue;
}
